import { useState, useEffect } from 'react';
import '../styles/global.css';

export default function Table({ columnas = [], datos = [], mensajeVacio = "Sin registros" }) {
  const [pagina, setPagina]       = useState(1);
  const [porPagina, setPorPagina] = useState(10);
  const [busqueda, setBusqueda]   = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [isMobile, setIsMobile]   = useState(window.innerWidth <= 768);
  const lista = Array.isArray(datos) ? datos : [];

  const filtrados = lista.filter(fila =>
    Object.values(fila).some(val =>
      String(val ?? '').toLowerCase().includes(busqueda.toLowerCase())
    )
  );

  const total    = filtrados.length;
  const paginas  = Math.ceil(total / porPagina);
  const inicio   = (pagina - 1) * porPagina;
  const visibles = filtrados.slice(inicio, inicio + porPagina);

  const handleBusqueda  = (v) => { setBusqueda(v); setPagina(1); };
  const handlePorPagina = (v) => { setPorPagina(Number(v)); setPagina(1); };

  // Columnas que no son de foto para la vista móvil
  const colsNormales = columnas.filter(col =>
    col.header !== 'Foto' && col.header !== 'Acciones' && col.header !== 'Vacaciones'
  );
  const colFoto      = columnas.find(col => col.header === 'Foto');
  const colAcciones  = columnas.find(col => col.header === 'Acciones');
  const colVacaciones = columnas.find(col => col.header === 'Vacaciones');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!e.ctrlKey) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); setPagina(p => Math.min(paginas, p + 1)); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); setPagina(p => Math.max(1, p - 1)); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [paginas]);

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <button className="btn-secondary" onClick={() => { setShowSearch(s => !s); setBusqueda(''); }}
          style={{ padding: '6px 10px' }} title="Buscar">🔍</button>
        {showSearch && (
          <input autoFocus className="form-input"
            style={{ width: 240, padding: '6px 10px', fontSize: 13 }}
            placeholder="Buscar en tabla..."
            value={busqueda} onChange={e => handleBusqueda(e.target.value)} />
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted, #888)' }}>
            {total === 0 ? '0 registros' : `${inicio + 1}–${Math.min(inicio + porPagina, total)} de ${total}`}
          </span>
          <select className="form-input" value={porPagina} onChange={e => handlePorPagina(e.target.value)}
            style={{ width: 'auto', padding: '4px 8px', fontSize: 12 }}>
            {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} por página</option>)}
          </select>
        </div>
      </div>

      {visibles.length === 0 ? (
        <p style={{ color: '#888', marginTop: '20px' }}>
          {busqueda ? `Sin resultados para "${busqueda}"` : mensajeVacio}
        </p>
      ) : isMobile ? (
        // ── Vista móvil — tarjetas ───────────────────────────────
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {visibles.map((fila, i) => (
            <div key={i} style={{
              border: '1px solid #ddd', borderRadius: 8, overflow: 'hidden',
              background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
            }}>
              {/* Header de la tarjeta: foto + primera columna */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', background: '#f9f9f9',
                borderBottom: '1px solid #eee' }}>
                {colFoto && (
                  <div style={{ flexShrink: 0 }}>
                    {colFoto.render(fila)}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {colsNormales[0] && (
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {colsNormales[0].render
                        ? colsNormales[0].render(fila)
                        : fila[colsNormales[0].accessor] ?? '—'}
                    </div>
                  )}
                  {colsNormales[1] && (
                    <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                      {colsNormales[1].render
                        ? colsNormales[1].render(fila)
                        : fila[colsNormales[1].accessor] ?? '—'}
                    </div>
                  )}
                </div>
              </div>

              {/* Datos restantes */}
              <div style={{ padding: '8px 12px' }}>
                {colsNormales.slice(2).map((col, j) => (
                  <div key={j} style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', padding: '4px 0',
                    borderBottom: j < colsNormales.slice(2).length - 1 ? '1px solid #f5f5f5' : 'none' }}>
                    <span style={{ fontSize: 11, color: '#888', fontWeight: 500 }}>{col.header}</span>
                    <span style={{ fontSize: 12, color: '#333' }}>
                      {col.render ? col.render(fila) : fila[col.accessor] ?? '—'}
                    </span>
                  </div>
                ))}
              </div>

              {/* Botones de acción al fondo */}
              {(colAcciones || colVacaciones) && (
                <div style={{ padding: '8px 12px', borderTop: '1px solid #eee',
                  display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {colVacaciones && colVacaciones.render(fila)}
                  {colAcciones  && colAcciones.render(fila)}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        // ── Vista desktop — tabla normal ──────────────────────────
        <table className="table">
          <thead>
            <tr>
              {columnas.map((col, i) => <th key={i}>{col.header}</th>)}
            </tr>
          </thead>
          <tbody>
            {visibles.map((fila, i) => (
              <tr key={i}>
                {columnas.map((col, j) => (
                  <td key={j}>
                    {col.render ? col.render(fila) : fila[col.accessor] ?? '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Paginación */}
      {paginas > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '0.75rem', flexWrap: 'wrap' }}>
          <button className="btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }}
            onClick={() => setPagina(1)} disabled={pagina === 1}>{'<<'}</button>
          <button className="btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }}
            onClick={() => setPagina(p => p - 1)} disabled={pagina === 1}>{'<'}</button>

          {Array.from({ length: paginas }, (_, i) => i + 1)
            .filter(p => p === 1 || p === paginas || Math.abs(p - pagina) <= 2)
            .reduce((acc, p, i, arr) => {
              if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) => p === '...'
              ? <span key={i} style={{ padding: '0 4px', fontSize: 12, color: '#888' }}>…</span>
              : <button key={i}
                  className={pagina === p ? 'btn-primary' : 'btn-secondary'}
                  style={{ padding: '4px 10px', fontSize: 12 }}
                  onClick={() => setPagina(p)}>{p}</button>
            )
          }

          <button className="btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }}
            onClick={() => setPagina(p => p + 1)} disabled={pagina === paginas}>{'>'}</button>
          <button className="btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }}
            onClick={() => setPagina(paginas)} disabled={pagina === paginas}>{'>>'}</button>
        </div>
      )}
    </div>
  );
}